import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileWarning, RefreshCw } from 'lucide-react';

import type { AuditEntry } from '@tasks/contracts';

import { listAuditEntries, listUsers } from '../../api/users';
import { queryKeys } from '../../api/queryKeys';
import { Avatar } from '../../components/Avatar';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { Input } from '../../components/Input';
import { Page, PageHeader } from '../../components/Page';
import { Select, type SelectOption } from '../../components/Select';
import { Skeleton } from '../../components/Skeleton';
import { Table, type TableColumn } from '../../components/Table';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { humanizeError } from '../../lib/errors';
import { formatDateTime } from '../../lib/format';
import styles from './AdminPages.module.css';

const PAGE_SIZE = 50;

/** Известные действия журнала — для фильтра и человекочитаемых подписей. */
const ACTION_LABELS: Readonly<Record<string, string>> = {
  'project.created': 'Создан проект',
  'project.updated': 'Изменён проект',
  'project.archived': 'Проект архивирован',
  'project.unarchived': 'Проект разархивирован',
  'project.deleted': 'Проект удалён',
  'board.created': 'Создана доска',
  'board.updated': 'Изменена доска',
  'board.deleted': 'Удалена доска',
  'board.reordered': 'Изменён порядок досок',
  'issue.created': 'Создана задача',
  'issue.updated': 'Изменена задача',
  'issue.deleted': 'Удалена задача',
  'issue.transitioned': 'Смена статуса',
  'issue.duplicated': 'Дублирование задачи',
  'user.created': 'Создан пользователь',
  'user.updated': 'Изменён пользователь',
  'user.deactivated': 'Пользователь отключён',
  'user.activated': 'Пользователь включён',
  'agent.deleted': 'AI-агент удалён',
  'auth.login': 'Вход в систему',
  'auth.login_failed': 'Неудачный вход',
  'auth.logout': 'Выход',
  'auth.password_changed': 'Смена пароля',
  'member.added': 'Добавлен участник',
  'member.updated': 'Изменена роль участника',
  'member.removed': 'Удалён участник',
  'sprint.started': 'Спринт начат',
  'sprint.completed': 'Спринт завершён',
};

/** `/admin/audit` — журнал действий с фильтрами (docs/06 §3.7, V1). */
export function AuditPage() {
  const [actorId, setActorId] = useState<string | null>(null);
  const [action, setAction] = useState<string | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [rawQuery, setRawQuery] = useState('');
  const query = useDebouncedValue(rawQuery, 300);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    setOffset(0);
  }, [actorId, action, from, to, query]);

  const usersQuery = useQuery({
    queryKey: queryKeys.admin.users('', 'all', 0),
    queryFn: ({ signal }) => listUsers({ limit: 100, offset: 0 }, signal),
  });

  const filters = JSON.stringify({ actorId, action, from, to, query, offset });

  const auditQuery = useQuery({
    queryKey: queryKeys.admin.audit(filters),
    queryFn: ({ signal }) =>
      listAuditEntries(
        {
          actorId: actorId ?? undefined,
          action: action ?? undefined,
          from: from.length > 0 ? `${from}T00:00:00.000Z` : undefined,
          to: to.length > 0 ? `${to}T23:59:59.999Z` : undefined,
          limit: PAGE_SIZE,
          offset,
        },
        signal,
      ),
  });

  const actorOptions: readonly SelectOption<string>[] = (usersQuery.data?.items ?? []).map((user) => ({
    value: user.id,
    label: user.displayName,
  }));

  const actionOptions: readonly SelectOption<string>[] = Object.keys(ACTION_LABELS).map((value) => ({
    value,
    label: ACTION_LABELS[value] ?? value,
  }));

  const columns: readonly TableColumn<AuditEntry>[] = [
    {
      id: 'createdAt',
      header: 'Время',
      width: '170px',
      render: (entry) => <span className="u-mono">{formatDateTime(entry.createdAt)}</span>,
    },
    {
      id: 'actor',
      header: 'Автор',
      width: '200px',
      render: (entry) =>
        entry.actor ? (
          <span className={styles['userCell']}>
            <Avatar user={entry.actor} size="xs" />
            {entry.actor.displayName}
          </span>
        ) : (
          <span className="u-muted">система</span>
        ),
    },
    {
      id: 'action',
      header: 'Действие',
      width: '220px',
      render: (entry) => <Badge>{ACTION_LABELS[entry.action] ?? entry.action}</Badge>,
    },
    {
      id: 'entity',
      header: 'Объект',
      width: '180px',
      render: (entry) => (
        <span className="u-mono u-muted">
          {entry.entityType}
          {entry.entityId ? ` · ${entry.entityId.slice(0, 8)}` : ''}
        </span>
      ),
    },
    {
      id: 'payload',
      header: 'Детали',
      render: (entry) => <span className={styles['payload']}>{JSON.stringify(entry.payload)}</span>,
    },
  ];

  const total = auditQuery.data?.total ?? 0;

  return (
    <Page width="wide">
      <PageHeader
        title="Журнал действий"
        actions={
          <Button
            variant="secondary"
            icon={<RefreshCw size={14} />}
            loading={auditQuery.isFetching}
            onClick={() => void auditQuery.refetch()}
          >
            Обновить
          </Button>
        }
      />

      <div className={styles['toolbar']}>
        <div className={styles['filterBox']}>
          <Select
            label="Автор"
            options={actorOptions}
            value={actorId}
            placeholder="Любой"
            clearable
            onClear={() => setActorId(null)}
            onChange={setActorId}
          />
        </div>
        <div className={styles['filterBox']}>
          <Select
            label="Действие"
            options={actionOptions}
            value={action}
            placeholder="Любое"
            clearable
            onClear={() => setAction(null)}
            onChange={setAction}
          />
        </div>
        <div className={styles['filterBox']}>
          <Input label="С даты" type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
        </div>
        <div className={styles['filterBox']}>
          <Input label="По дату" type="date" value={to} onChange={(event) => setTo(event.target.value)} />
        </div>
        <div className={styles['searchBox']}>
          <Input
            label="Поиск по объекту"
            value={rawQuery}
            placeholder="Ключ задачи или проекта"
            onChange={(event) => setRawQuery(event.target.value)}
          />
        </div>
      </div>

      {auditQuery.isPending ? (
        <Skeleton variant="row" count={8} />
      ) : auditQuery.isError ? (
        <EmptyState
          icon={<FileWarning size={20} />}
          title="Не удалось загрузить журнал"
          description={humanizeError(auditQuery.error).message}
          action={
            <Button variant="primary" onClick={() => void auditQuery.refetch()}>
              Повторить
            </Button>
          }
        />
      ) : total === 0 ? (
        <EmptyState
          icon={<FileWarning size={20} />}
          title="Записей нет"
          description="Измените фильтры или дождитесь действий пользователей."
        />
      ) : (
        <>
          <div className={styles['tableWrap']}>
            <Table
              ariaLabel="Журнал действий"
              columns={columns}
              rows={filterByQuery(auditQuery.data?.items ?? [], query)}
              getRowId={(entry) => String(entry.id)}
            />
          </div>
          <div className={styles['pagination']}>
            <Button size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
              Назад
            </Button>
            <span className="u-muted">
              {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} из {total}
            </span>
            <Button size="sm" disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>
              Вперёд
            </Button>
          </div>
        </>
      )}
    </Page>
  );
}

/** Клиентская фильтрация по подстроке: API V1 не ищет по полезной нагрузке. */
function filterByQuery(entries: readonly AuditEntry[], query: string): readonly AuditEntry[] {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) {
    return entries;
  }
  return entries.filter(
    (entry) =>
      (entry.entityId ?? '').toLowerCase().includes(needle) ||
      entry.action.toLowerCase().includes(needle) ||
      entry.entityType.toLowerCase().includes(needle),
  );
}
